
import { useState } from "react";
import { Eye, Building, User, Mail, Calendar } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { AdminCampaign } from "../types";
import { getStatusColor, getStatusText } from "../utils";
import { CampaignActionButtons } from "./CampaignActionButtons";
import { CampaignDetailsDialog } from "./CampaignDetailsDialog";

interface CampaignCardProps {
  campaign: AdminCampaign;
  updating: string | null;
  onUpdateStatus: (campaignId: string, newStatus: 'approved' | 'rejected' | 'pending', rejectionReason?: string) => void;
  onDelete?: (campaignId: string) => void;
}

export const CampaignCard = ({ campaign, updating, onUpdateStatus, onDelete }: CampaignCardProps) => {
  const { language } = useLanguage();
  const [showDetails, setShowDetails] = useState(false);

  const calculateDuration = () => {
    if (!campaign.start_date || !campaign.end_date) return 0;
    const start = new Date(campaign.start_date);
    const end = new Date(campaign.end_date);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <>
      <Card className="bg-card/80 dark:bg-card/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-3 text-xl text-foreground">
                {campaign.brand_name || campaign.company_name}
                <Badge className={`${getStatusColor(campaign.status)} border-0 shadow-sm`}>
                  {getStatusText(campaign.status, language)}
                </Badge>
                <span className="text-xs text-muted-foreground">ID: {campaign.id.slice(0, 8)}</span>
              </CardTitle>
              <CardDescription className="mt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <span className="flex items-center gap-1">
                    <Building className="h-4 w-4" />
                    {campaign.company_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {campaign.person_in_charge} - {campaign.job_title}
                  </span>
                  <span className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    {campaign.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date(campaign.created_at).toLocaleDateString()} {new Date(campaign.created_at).toLocaleTimeString()}
                  </span>
                </div>
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDetails(true)}>
                <Eye className="h-4 w-4" />
              </Button>
              
              <CampaignActionButtons 
                campaign={campaign}
                updating={updating}
                onUpdateStatus={onUpdateStatus}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-600">
                {language === 'ar' ? 'الميزانية' : 'Budget'}
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {campaign.total_budget?.toLocaleString() || 0} {language === 'ar' ? 'ر.س' : 'SAR'}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-600">
                {language === 'ar' ? 'الأجهزة' : 'Devices'}
              </div>
              <div className="text-2xl font-bold text-slate-900">{campaign.total_devices || 0}</div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-600">
                {language === 'ar' ? 'المدة' : 'Duration'}
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {calculateDuration()} {language === 'ar' ? 'يوم' : 'days'}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-600">
                {language === 'ar' ? 'المواقع' : 'Locations'}
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {Array.isArray(campaign.selected_locations) ? campaign.selected_locations.length : 0}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <CampaignDetailsDialog 
        campaign={campaign}
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        onUpdateStatus={onUpdateStatus}
        onDelete={onDelete}
        updating={updating}
      />
    </>
  );
};
